import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import {
  BackendTransaction,
  BasicTransactionType,
  MerkleTransactionUpdateNetworksType,
  OrgAssetTransfer,
  OrgMerkleTransaction,
  OrgSmartAccount,
} from '~/types'

export const useAccountUpdatedByMerkleTxn = (
  tx: Pick<BackendTransaction | OrgAssetTransfer, 'titleChunks' | 'chainId'>,
  options?: { includeAutomations?: boolean },
): OrgSmartAccount | undefined => {
  const { smartAccounts } = useSmartAccounts({
    includeAutomations: options?.includeAutomations,
  })

  // first check if it's a merkle transaction
  if (!('isMerkleTransaction' in tx && tx.isMerkleTransaction)) {
    return undefined
  }

  const merkleTxn = tx as OrgMerkleTransaction

  // if it's a recovery txn, pull from title chunks
  if (merkleTxn.types.includes(BasicTransactionType.RecoverAccount)) {
    // get the smartAccuontId from the first child transaction
    // @ts-expect-error - smartAccountId is not in the type for some reason
    const smartAccountId = merkleTxn.txData.transactions?.at(0)?.smartAccountId
    if (!smartAccountId) return undefined
    const account = smartAccounts.find((a) => a.id === smartAccountId)
    return account
  }

  // if it's a network update, it'll be in the stateSync
  if (merkleTxn.types.includes(BasicTransactionType.AddNetworks)) {
    const address = merkleTxn.txData.transactions.find(
      (tx) => tx.type === MerkleTransactionUpdateNetworksType.UpdateNetworks,
    )?.stateSyncs?.[0]?.smartAccountAddress
    return smartAccounts.find((account) => account.address === address)
  }

  // if it's a signer update, it's just the account address
  const address = merkleTxn.smartAccountAddress
  return smartAccounts.find((account) => account.address === address)
}
